import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
} from "@tanstack/react-router";
import { ClerkProvider } from "@clerk/tanstack-react-start";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import * as React from "react";
import { DefaultCatchBoundary } from "~/components/DefaultCatchBoundary";
import { NotFound } from "~/components/NotFound";
import { TooltipProvider } from "~/components/ui/tooltip";
import CSS from "~/styles/globals.css?url";
import FontInterVariable from "@fontsource-variable/inter/opsz.css?url";
import FontInterVariableItalic from "@fontsource-variable/inter/opsz-italic.css?url";
import FontDMSans from "@fontsource-variable/dm-sans?url";
import { context, defaultTextMapSetter, propagation } from "@opentelemetry/api";
import { Toaster } from "sonner";
import * as Sentry from "@sentry/browser";
import { wrapCreateRootRouteWithSentry } from "@sentry/tanstackstart-react";
import { clientEnv } from "~/lib/utils";

import type { QueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { configureBuilder } from "~/components/builder-registry";
import { createServerFn } from "@tanstack/react-start";
import { getWebRequest } from "@tanstack/react-start/server";

export const fetchClerkAuth = createServerFn({ method: "GET" }).handler(
  async () => {
    const { getAuth } = await import("@clerk/tanstack-react-start/server");
    const { userId } = await getAuth(getWebRequest()!);

    return {
      userId,
    };
  }
);

export const Route = wrapCreateRootRouteWithSentry(createRootRouteWithContext)<{
  queryClient: QueryClient;
}>()({
  head: () => {
    const carrier: { [key: string]: string } = {};
    propagation.inject(context.active(), carrier, defaultTextMapSetter);

    return {
      meta: [
        {
          charSet: "utf-8",
        },
        {
          name: "viewport",
          content: "width=device-width, initial-scale=1",
        },
        ...Object.entries(carrier).map(([k, v]) => ({
          name: k,
          content: v,
        })),
      ],
      links: [
        {
          rel: "apple-touch-icon",
          sizes: "180x180",
          href: "/apple-touch-icon.png",
        },
        {
          rel: "icon",
          type: "image/png",
          sizes: "32x32",
          href: "/favicon-32x32.png",
        },
        {
          rel: "icon",
          type: "image/png",
          sizes: "16x16",
          href: "/favicon-16x16.png",
        },
        { rel: "manifest", href: "/site.webmanifest", color: "#fffff" },
        { rel: "icon", href: "/favicon.ico" },
        { rel: "stylesheet", href: CSS },
        { rel: "stylesheet", href: FontInterVariable },
        { rel: "stylesheet", href: FontInterVariableItalic },
        { rel: "stylesheet", href: FontDMSans },
      ],
      scripts: [
        {
          type: "text/javascript",
          src: "https://www.googletagmanager.com/gtag/js?id=GTM-W7GK66ML",
        },
        {
          type: "text/javascript",
          children: `
            window.dataLayer = window.dataLayer || [];
            window.gtag = function() {
              dataLayer.push(arguments);
            }
            gtag('js', new Date());
            gtag('config', 'GTM-W7GK66ML');
          `,
        },
        {
          type: "text/javascript",
          children: `
            var sbSiteSecret = "2b79a9d3-94bc-4d56-a7a0-96e0e1f7a7db";
            window.sitebehaviourTrackingSecret = sbSiteSecret;
          `,
        },
        {
          type: "text/javascript",
          src: "https://sitebehaviour-cdn.fra1.cdn.digitaloceanspaces.com/index.min.js?sitebehaviour-secret=2b79a9d3-94bc-4d56-a7a0-96e0e1f7a7db",
        },
        {
          type: "text/javascript",
          children: `
            (function(c,l,a,r,i,t,y){
                c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
            })(window, document, "clarity", "script", "petk9fvk1i");
          `,
        },
        {
          type: "text/javascript",
          children: `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '1555911545046104');
            fbq('track', 'PageView');
          `,
        },
      ],
    };
  },
  errorComponent: (props) => {
    return (
      <RootDocument>
        <DefaultCatchBoundary {...props} />
      </RootDocument>
    );
  },
  notFoundComponent: () => <NotFound />,
  component: RootComponent,
});

function RootComponent() {
  return (
    <ClerkProvider publishableKey={clientEnv.VITE_CLERK_PUBLISHABLE_KEY}>
      <TooltipProvider>
        <RootDocument>
          <Outlet />
        </RootDocument>
      </TooltipProvider>
    </ClerkProvider>
  );
}

function RootDocument({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    configureBuilder();

    if (import.meta.env.DEV) {
      return;
    }

    Sentry.init({
      dsn: "https://7eee74b9e1beef52e3dec4b648261396@o4508840544698368.ingest.de.sentry.io/4508840773550160",
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
      ],
      allowUrls: ["localhost", /^https:\/\/dreamcoachmatch\.com/],
      // Tracing
      tracesSampleRate: 1.0, //  Capture 100% of the transactions
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: ["localhost", /^https:\/\/dreamcoachmatch\.com/],
      // Session Replay
      replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
      replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    });
  }, []);

  return (
    <html>
      <head>
        <HeadContent />
      </head>
      <body className="font-sans">
        {children}
        <TanStackRouterDevtools position="bottom-right" />
        <Scripts />
        <Toaster className="fixed" richColors />
      </body>
    </html>
  );
}
